import { gql } from "@apollo/client";

export const GET_TRANSACTION = gql`
  query transaction($id: ID!) {
    transaction(id: $id) {
      id
      company {
        businessLegalName
      }
      user {
        id
        firstName
        lastName
        email
        phone
        address {
          address1
          address2
          addressName
          city
          state
          country
          zipcode
        }
        status
        manager {
          firstName
          lastName
        }
      }
      policyStatusDisplay
      isLostApprovalAllowed
      policyStatus {
        receipt {
          violationStatus
          isExempt
          violationDate
          overriddenDate
          overriddenBy {
            firstName
            lastName
          }
        }
        allocation {
          violationStatus
          isExempt
          violationDate
          overriddenDate
          overriddenBy {
            firstName
            lastName
          }
        }
      }
      allocationCode {
        id
        allocationCode
      }
      card {
        type
        nickname
        cardNumber
        status
      }
      expenseCategory {
        id
        name
        isSuggestion
      }
      memo
      memoUpdatedAt
      type
      description
      transactionId
      transactionDate
      createdAt
      amount
      rewardsAmount
      refundAmount
      currency
      status
      merchant
      merchantData {
        url
        logo
        cachedLogo
        country
        city
        state
        postal_code
        merchantId
        name
      }
      authorizationId
      disputeStatus
      disputeAmount
      receiptDetail {
        companyName
        id
        assets {
          assetUrl
          id
          extension
        }
      }
      allocationType {
        key
        name
      }
      isIntegrationSynced
      isSyncEligible
      failureReason
      fees {
        feeType
        feeAmount
      }
      linkedInvoices {
        id
        invoiceName
        invoiceNumber
      }
      linkedTransactions {
        id
        type
        merchant
        merchantData {
          url
          logo
          cachedLogo
        }
        amount
        currency
      }
      linkedPayments {
        id
        payTo {
          id
          firstName
          lastName
        }
      }
      customFields {
        fieldSettingId
        values
      }
      workflowRequest {
        id
        status
        approvalType
        approvedBy {
          userId
          userName
          role
        }
        approvedDate
        rejectedBy {
          userId
          userName
          role
        }
        canceledBy {
          userId
          userName
          role
        }
        canceledDate
        rejectedDate
        remainingApprovalRoles
        userId
        isApprovalAllowed
      }
      mileage {
        miles
        tripFrom {
          addressName
          address1
          address2
          city
          state
          country
          zipcode
          geocode {
            x
            y
          }
        }
        tripTo {
          addressName
          address1
          address2
          city
          state
          country
          zipcode
          geocode {
            x
            y
          }
        }
        isRoundTrip
        polyline
      }
      owningAccount {
        name
        accountType
        last4
      }
      travelData {
        airline {
          recordLocator
          ticketNumber
          passengerName
        }
      }
    }
  }
`;

export const GET_TRANSACTIONS = gql`
  query transactions(
    $status: Status
    $type: [TransactionType]
    $first: Int
    $after: String
    $amount: AmountQuery
    $date: DatePeriod
    $startDate: Float
    $endDate: Float
    $transactionStatus: [Status]
    $compliances: [TransactionCompliance]
    $cardHolder: String
    $cardId: String
    $searchTerm: String
    $allocationType: AllocationTypeInput
    $allocationId: String
    $accountId: String
    $cardType: [CardType]
    $mine: Boolean
  ) {
    transactions(
      status: $status
      type: $type
      amount: $amount
      transactionStatus: $transactionStatus
      compliances: $compliances
      cardHolder: $cardHolder
      cardId: $cardId
      date: $date
      startDate: $startDate
      endDate: $endDate
      searchTerm: $searchTerm
      allocationType: $allocationType
      allocationId: $allocationId
      first: $first
      after: $after
      accountId: $accountId
      cardType: $cardType
      mine: $mine
    ) {
      edges {
        cursor
        node {
          id
          user {
            id
            firstName
            lastName
            email
          }
          card {
            type
            nickname
            cardNumber
            status
          }
          type
          transactionDate
          createdAt
          amount
          rewardsAmount
          currency
          transactionId
          merchant
          status
          disputeStatus
          disputeAmount
          memo
          policyStatusDisplay
          isIntegrationSynced
          isSyncEligible
          syncError
          isLostApprovalAllowed
          policyStatus {
            receipt {
              violationStatus
              isExempt
            }
            allocation {
              violationStatus
              isExempt
            }
          }
          expenseCategory {
            id
            name
          }
          allocationCode {
            id
            allocationCode
          }
          description
          merchant
          merchantData {
            url
            logo
            cachedLogo
            country
            city
            state
            postal_code
            merchantId
            name
          }
          receiptDetail {
            companyName
          }
          allocationType {
            key
            name
          }
          fees {
            feeType
            feeAmount
          }
          linkedInvoices {
            id
            invoiceName
            invoiceNumber
          }
          workflowRequest {
            status
          }
          owningAccount {
            name
            accountType
            last4
          }
        }
      }
      pageInfo {
        hasNextPage
        endCursor
        total
      }
    }
  }
`;

export const GET_TRANSACTION_REFUNDABLE_AMOUNT = gql`
  query transactionRefundableAmount($id: ID!) {
    transaction(id: $id) {
      id
      refundableAmount
    }
  }
`;

export const GET_TRANSACTION_STATUSES = gql`
  query transactionStatuses(
    $type: [TransactionType]
    $first: Int
    $transactionStatus: [Status]
    $mine: Boolean
  ) {
    transactions(
      type: $type
      transactionStatus: $transactionStatus
      first: $first
      mine: $mine
    ) {
      edges {
        node {
          id
          status
        }
      }
      pageInfo {
        hasNextPage
        total
      }
    }
  }
`;

export const GET_TRANSACTION_ROWS = gql`
  query transactionRows(
    $status: Status
    $type: [TransactionType]
    $first: Int
    $after: String
    $amount: AmountQuery
    $date: DatePeriod
    $startDate: Float
    $endDate: Float
    $transactionStatus: [Status]
    $compliances: [TransactionCompliance]
    $cardHolder: String
    $cardId: String
    $searchTerm: String
    $allocationType: AllocationTypeInput
    $allocationId: String
    $accountId: String
    $mine: Boolean
    $withWorkflow: Boolean = false
    $withPersonalActions: Boolean = false
    $withAdminColumns: Boolean = false
  ) {
    transactions(
      status: $status
      type: $type
      amount: $amount
      transactionStatus: $transactionStatus
      compliances: $compliances
      cardHolder: $cardHolder
      cardId: $cardId
      date: $date
      startDate: $startDate
      endDate: $endDate
      searchTerm: $searchTerm
      allocationType: $allocationType
      allocationId: $allocationId
      first: $first
      after: $after
      accountId: $accountId
      mine: $mine
    ) {
      edges {
        node {
          id
          merchantData {
            url
            logo
            cachedLogo
            country
            city
            state
            postal_code
            merchantId
            name
          }
          amount
          currency
          transactionDate
          createdAt
          merchant
          status
          type
          disputeStatus
          memo
          linkedTransactions @include(if: $withPersonalActions) {
            id
            type
          }
          user @include(if: $withAdminColumns) {
            id
            firstName
            lastName
            profilePicUrl
          }
          owningAccount @include(if: $withAdminColumns) {
            name
            accountType
            last4
          }
          isIntegrationSynced @include(if: $withAdminColumns)
          isSyncEligible @include(if: $withAdminColumns)
          syncError @include(if: $withAdminColumns)
          policyStatusDisplay
          policyStatus {
            receipt {
              violationStatus
              isExempt
            }
            allocation {
              violationStatus
              isExempt
            }
          }
          card {
            id
            type
            nickname
            cardNumber
            status
          }
          expenseCategory {
            id
            name
          }
          receiptDetail {
            id
          }
          workflowRequest @include(if: $withWorkflow) {
            status
          }
        }
      }
      pageInfo {
        hasNextPage
        endCursor
        total
      }
    }
  }
`;

export const GET_TRANSACTIONS_PENDING_MY_APPROVAL = gql`
  query transactionsPendingMyApproval(
    $first: Int
    $after: String
    $searchTerm: String
  ) {
    transactionsPendingMyApproval(
      first: $first
      after: $after
      searchTerm: $searchTerm
    ) {
      edges {
        cursor
        node {
          id
          user {
            id
            firstName
            lastName
            email
          }
          card {
            type
            nickname
            cardNumber
            status
          }
          type
          transactionDate
          createdAt
          amount
          rewardsAmount
          currency
          transactionId
          merchant
          status
          disputeStatus
          disputeAmount
          memo
          policyStatusDisplay
          isIntegrationSynced
          isSyncEligible
          syncError
          isLostApprovalAllowed
          policyStatus {
            receipt {
              violationStatus
              isExempt
            }
            allocation {
              violationStatus
              isExempt
            }
          }
          expenseCategory {
            id
            name
          }
          allocationCode {
            id
            allocationCode
          }
          description
          merchantData {
            url
            logo
            cachedLogo
            country
            city
            state
            postal_code
            merchantId
            name
          }
          receiptDetail {
            companyName
          }
          allocationType {
            key
            name
          }
          fees {
            feeType
            feeAmount
          }
          linkedInvoices {
            id
            invoiceName
            invoiceNumber
          }
          workflowRequest {
            status
          }
          owningAccount {
            name
            accountType
            last4
          }
        }
      }
      pageInfo {
        hasNextPage
        endCursor
        total
      }
    }
  }
`;

export const GET_TRANSACTIONS_FOR_DUPLICATE_CHECK = gql`
  query transactionsForDuplicateCheck(
    $type: [TransactionType]
    $date: DatePeriod
    $cardHolder: String
    $searchTerm: String
    $first: Int
  ) {
    transactions(
      type: $type
      date: $date
      cardHolder: $cardHolder
      searchTerm: $searchTerm
      first: $first
    ) {
      edges {
        node {
          id
          type
          transactionDate
          amount
          currency
          status
          merchant
          memo
          description
          merchantData {
            name
          }
          mileage {
            miles
            isRoundTrip
            tripFrom {
              addressName
              address1
              city
              state
              zipcode
              geocode {
                x
                y
              }
            }
            tripTo {
              addressName
              address1
              city
              state
              zipcode
              geocode {
                x
                y
              }
            }
          }
        }
      }
    }
  }
`;

export const GET_TRANSFERS = gql`
  query getTransfers($first: Int, $after: String, $before: String) {
    getTransfers(first: $first, after: $after, before: $before) {
      edges {
        cursor
        node {
          id
          transactionId
          merchant
          type
          transactionDate
          status
          amount
          currency
          user {
            id
            firstName
            lastName
          }
          memo
        }
      }
      pageInfo {
        hasNextPage
        endCursor
        total
      }
    }
  }
`;

export const GET_TRANSFER_DETAILS = gql`
  query getTransferDetails($id: String!) {
    getTransferDetails(id: $id) {
      id
      status
      amount
      currency
      receiptUrl
      created
      description
    }
  }
`;

export const GET_REWARDS_TRANSFERS = gql`
  query getRewardsTransfers($first: Int, $after: String, $before: String) {
    getRewardsTransfers(first: $first, after: $after, before: $before) {
      edges {
        cursor
        node {
          id
          transactionId
          merchant
          type
          transactionDate
          status
          amount
          currency
          user {
            id
            firstName
            lastName
          }
          memo
        }
      }
      pageInfo {
        hasNextPage
        endCursor
      }
    }
  }
`;

export const GET_LATEST_PENDING_TRANSACTION = gql`
  query latestTransactions($first: Int) {
    latestTransactions(first: $first) {
      edges {
        cursor
        node {
          id
          user {
            id
            firstName
            lastName
          }
          type
          transactionDate
          amount
          currency
          status
          allocationCode {
            id
            allocationCode
          }
          merchant
          policyStatusDisplay
          isIntegrationSynced
          isSyncEligible
          syncError
          isLostApprovalAllowed
          policyStatus {
            receipt {
              violationStatus
              isExempt
            }
            allocation {
              violationStatus
              isExempt
            }
          }
          allocationType {
            key
            name
          }
          customFields {
            fieldSettingId
            values
          }
        }
      }
      pageInfo {
        hasNextPage
        endCursor
        total
      }
    }
  }
`;

export const GET_TOP_SPENDING = gql`
  query topSpending(
    $accountId: String
    $userId: String
    $cardId: String
    $expenseCategoryId: String
    $merchantId: String
    $allocationType: AllocationTypeInput
    $allocationId: String
    $dateDescriptor: String
    $groupKey: String
  ) {
    topSpending(
      accountId: $accountId
      userId: $userId
      cardId: $cardId
      expenseCategoryId: $expenseCategoryId
      merchantId: $merchantId
      allocationType: $allocationType
      allocationId: $allocationId
      dateDescriptor: $dateDescriptor
      groupKey: $groupKey
    ) {
      data {
        name
        monthKey
        categoryAmounts {
          categoryName
          amount
        }
      }
      currency
      keys
    }
  }
`;

export const GET_PIE_CHART_SPENDING = gql`
  query pieChartSpending(
    $accountId: String
    $userId: String
    $cardId: String
    $expenseCategoryId: String
    $merchantId: String
    $allocationType: AllocationTypeInput
    $allocationId: String
    $dateDescriptor: String
    $groupKey: String
  ) {
    pieChartSpending(
      accountId: $accountId
      userId: $userId
      cardId: $cardId
      expenseCategoryId: $expenseCategoryId
      merchantId: $merchantId
      allocationType: $allocationType
      allocationId: $allocationId
      dateDescriptor: $dateDescriptor
      groupKey: $groupKey
    ) {
      data {
        name
        value
      }
      currency
      totalValue
    }
  }
`;

export const GET_CASH_FLOW = gql`
  query getCashFlow(
    $date: DatePeriod
    $transactionStatus: [Status]
    $type: [TransactionType]
    $accountId: String
  ) {
    getCashFlow(
      date: $date
      transactionStatus: $transactionStatus
      type: $type
      accountId: $accountId
    ) {
      currency
      cashFlowData {
        date
        balance
      }
    }
  }
`;

export const GET_TRANSACTIONS_BY_ALLOCATION = gql`
  query transactionsByAllocation(
    $allocationType: AllocationTypeInput
    $allocationId: String
  ) {
    transactions(allocationType: $allocationType, allocationId: $allocationId) {
      edges {
        cursor
        node {
          id
          user {
            id
            firstName
            lastName
          }
          card {
            type
            nickname
            cardNumber
            status
          }

          transactionDate
          amount
          currency
          transactionId
          merchant
          merchantData {
            url
            logo
            cachedLogo
            country
            city
            state
            postal_code
            merchantId
            name
          }
          receiptDetail {
            companyName
            id
            assets {
              assetUrl
              id
              extension
            }
          }
          customFields {
            fieldSettingId
            values
          }
        }
      }
      pageInfo {
        hasNextPage
        endCursor
        total
      }
    }
  }
`;

export const UPDATE_VIOLATION_POLICY_STATUS = gql`
  mutation updateTransactionPolicyStatus(
    $id: ID!
    $input: InputViolationPolicyStatus
  ) {
    updateTransactionPolicyStatus(id: $id, input: $input) {
      id
      user {
        firstName
        lastName
        email
        address {
          city
        }
        status
        manager {
          firstName
          lastName
        }
      }
      policyStatusDisplay
      isLostApprovalAllowed
      policyStatus {
        receipt {
          violationStatus
          isExempt
          violationDate
          overriddenDate
          overriddenBy {
            firstName
            lastName
          }
        }
        allocation {
          violationStatus
          isExempt
          violationDate
          overriddenDate
          overriddenBy {
            firstName
            lastName
          }
        }
      }
      allocation {
        category
        name
        code
      }
      card {
        type
        nickname
        cardNumber
        status
      }
      category {
        categoryName
      }
      memo
      type
      transactionDate
      amount
      status
      merchant
      receiptDetail {
        companyName
      }
    }
  }
`;

export const UPDATE_TRANSACTION = gql`
  mutation updateTransaction($id: ID!, $input: UpdateTransactionInput!) {
    updateTransaction(id: $id, input: $input) {
      id
    }
  }
`;

export const REMOVE_RECEIPT_ASSET = gql`
  mutation removeReceiptAsset($receiptId: ID!, $assetId: ID!) {
    removeReceiptAsset(receiptId: $receiptId, assetId: $assetId) {
      id
    }
  }
`;

export const MARK_RECEIPT_AS_LOST = gql`
  mutation markReceiptAsLost($transactionId: ID!) {
    markReceiptAsLost(transactionId: $transactionId)
  }
`;

export const MARK_LOST_RECEIPT_AS_APPROVED = gql`
  mutation markLostReceiptAsApproved($transactionId: ID!) {
    markLostReceiptAsApproved(transactionId: $transactionId)
  }
`;

export const APPROVE_MISMATCH_RECEIPT = gql`
  mutation approveMismatchReceipt($transactionId: ID!) {
    approveMismatchReceipt(transactionId: $transactionId)
  }
`;

export const REJECT_MISMATCH_RECEIPT = gql`
  mutation rejectMismatchReceipt($transactionId: ID!) {
    rejectMismatchReceipt(transactionId: $transactionId)
  }
`;

export const SEND_TRANSACTION_REMINDER = gql`
  mutation sendTransactionReminder($id: ID!) {
    sendTransactionReminder(id: $id) {
      isSuccess
    }
  }
`;

export const REQUEST_PERSONAL_REIMBURSEMENT = gql`
  mutation requestPersonalReimbursement(
    $input: PersonalReimbursementInput!
    $expenseId: ID
  ) {
    requestPersonalReimbursement(input: $input, expenseId: $expenseId) {
      id
      amount
      status
      payTo {
        id
        firstName
        lastName
      }
      mileage {
        tripFrom {
          addressName
        }
        tripTo {
          addressName
        }
        miles
        isRoundTrip
        polyline
      }
    }
  }
`;

export const APPROVE_PERSONAL_REIMBURSEMENT = gql`
  mutation approvePersonalReimbursement($requestId: ID!) {
    approvePersonalReimbursement(requestId: $requestId) {
      id
      status
    }
  }
`;

export const REJECT_PERSONAL_REIMBURSEMENT = gql`
  mutation rejectPersonalReimbursement($requestId: ID!) {
    rejectPersonalReimbursement(requestId: $requestId) {
      id
      status
    }
  }
`;

export const CANCEL_PERSONAL_REIMBURSEMENT = gql`
  mutation cancelPersonalReimbursement($transactionId: ID!, $requestId: ID) {
    cancelPersonalReimbursement(
      transactionId: $transactionId
      requestId: $requestId
    ) {
      id
      status
    }
  }
`;

export const MARK_TRANSACTION_AS_PERSONAL_EXPENSE = gql`
  mutation markTransactionAsPersonalExpense(
    $transactionId: ID!
    $personalExpenseComment: String
  ) {
    markTransactionAsPersonalExpense(
      transactionId: $transactionId
      personalExpenseComment: $personalExpenseComment
    ) {
      id
      status
    }
  }
`;

export const PAY_PERSONAL_EXPENSE = gql`
  mutation payPersonalExpense($transactionId: ID!) {
    payPersonalExpense(transactionId: $transactionId) {
      id
      status
    }
  }
`;

export const CANCEL_PERSONAL_EXPENSE = gql`
  mutation cancelPersonalExpense($transactionId: ID!) {
    cancelPersonalExpense(transactionId: $transactionId) {
      id
      status
    }
  }
`;

export const TRANSACTION_TASKS_BY_COMPLIANCE = gql`
  query transactionTasksByCompliance(
    $compliance: TransactionCompliance
    $first: Int
  ) {
    transactionTasks(compliance: $compliance, first: $first) {
      pageInfo {
        total
      }
    }
  }
`;

/**
 * Lean transaction feed for the Astrada Linked Accounts table. `card { id }` is what scopes
 * it, since `accountId` cannot filter by connection.
 *
 * See client/src/components/admin/linkedAccounts/README.md.
 */
export const ASTRADA_TRANSACTIONS = gql`
  query astradaTransactions($first: Int, $after: String) {
    transactions(first: $first, after: $after) {
      edges {
        cursor
        node {
          id
          transactionDate
          merchant
          amount
          currency
          user {
            id
            firstName
            lastName
          }
          card {
            id
            cardNumber
          }
        }
      }
      pageInfo {
        hasNextPage
        endCursor
        total
      }
    }
  }
`;
